import React, { useEffect, useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, GridApi, IRowNode, RowDataUpdatedEvent } from 'ag-grid-community';
import { Grid } from '@mui/material';

import IPivotChildColumn from '../../../../../../../../shared/contracts/IPivotChildColumn';
import useWizardPivotChildColumnService from './wizard-pivot-child-columns.service';
import AgGridService from '../../../../../../../../shared/services/ag-grid/ag-grid.service';
import useWizardReportDataContext from '../../../../../contexts/useWizardReportData.context';
import ChildGridAddRowButtonOverlayComponent from './cell-renderers/child-grid-add-row-button-overlay.component';
import useHelpers from '../../../../../../../../shared/services/helpers/useHelpers.hook';


/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Jan/05/2025
 */
export default function WizardPivotChildColumns({ }: {}) {

    const { checkIsDifferentOnSameType } = useHelpers();
    const { availableColumns, pivotData, setPivotData } = useWizardReportDataContext();
    const { getColDefs, insertRow, initializeRows } = useWizardPivotChildColumnService();
    const { getDefaultColDef, getDefaultProps } = new AgGridService();
    const [gridApi, setGridApi] = useState<GridApi<IPivotChildColumn>>();
    const [initialized, setInitialized] = useState<boolean>(false);

    const noRowsOverlayComponentParams = useMemo(() => {
        return {
            onAddClick: () => gridApi && insertRow(gridApi, 0)
        };
    }, [gridApi]);

    useEffect(() => {
        if (isDifferent(gridApi)) {
            initializeRows(gridApi, pivotData?.childColumns);
        }
    }, [gridApi, availableColumns, pivotData?.childColumns]);

    const isDifferent = (gridApi: GridApi<IPivotChildColumn> | undefined): boolean => {
        if (gridApi) {
            const gridChildColumns: IPivotChildColumn[] = gridApi.getRenderedNodes()
                .filter((node): node is IRowNode<IPivotChildColumn> => !!node.data)
                .map((node) => node.data!);

            const isDifferent = checkIsDifferentOnSameType(pivotData?.childColumns, gridChildColumns);
            // console.log({ isDifferent });
            return isDifferent;
        }
        return false;
    }

    const handleDataChange = (
        e: CellValueChangedEvent<IPivotChildColumn, any> | RowDataUpdatedEvent<IPivotChildColumn>
    ) => {

        const childColumns: IPivotChildColumn[] = [];
        e.api.forEachNode((node) => {
            if (node.data) {
                childColumns.push({ ...node.data })
            }
        })

        // update the context
        setPivotData((prev) => {
            return {
                ...prev,
                childColumns: !childColumns?.length ? null : childColumns
            }
        });
    }

    // --
    return (
        <Grid container direction="row" wrap="nowrap" sx={{ p: 0, pt: 3 }}>
            {/* The AG Grid component */}
            <div className={"ag-theme-quartz"}
                style={{ height: "420px", width: "100%" }}
            >
                <AgGridReact<IPivotChildColumn>
                    columnDefs={getColDefs}
                    defaultColDef={getDefaultColDef()}
                    {...getDefaultProps({
                        rowHeight: 44,
                        hideSideBar: true,
                        rowDragManaged: true,
                    })}
                    // for add-new-button
                    noRowsOverlayComponent={ChildGridAddRowButtonOverlayComponent}
                    noRowsOverlayComponentParams={noRowsOverlayComponentParams}
                    onGridReady={(e) => setGridApi(e.api)}
                    onRowDataUpdated={handleDataChange}
                />
            </div>
        </Grid>);
}